import React from 'react'
import { Trans } from 'react-i18next'

import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { MtwProvider } from '@makyoapp/makyo-tw-ui'
import { ColorPickerV2, IColor, useTheme } from '@makyoapp/makyo-ui'

import { useOrgPerm } from '@/hooks/useOrgPerm'
import { sourceToHex } from '@/shared/brand-kit'
import { useDebounceInput } from '@/shared/makyo-hooks'

import {
  AccordionDemo,
  AudioDemo,
  BarChartDemo,
  ButtonDemo,
  CardItemsDemo,
  CircleProgressDemo,
  DatatableDemo,
  FABDemo,
  ImageDemo,
  ListDemo,
  LocationDemo,
  PieChartDemo,
  RatingProgressDemo,
  VideoDemo,
} from './components'

const ComponentDemoLayout = React.memo(({ sourceColor }: { sourceColor: string }) => {
  const {
    state: { mode },
  } = useTheme()

  return (
    <MtwProvider sourceColor={sourceColor} extendedColors={[]} mode={mode}>
      <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 grid-auto-rows: 1fr'>
        <CardItemsDemo />
        <BarChartDemo />
        <PieChartDemo />
        <ImageDemo />
        <div className='col-span-2'>
          <DatatableDemo />
        </div>
        <ListDemo />
        <AudioDemo />
        <VideoDemo />
        <FABDemo />
        <ButtonDemo />
        <RatingProgressDemo />
        <CircleProgressDemo />
        <LocationDemo />
        <AccordionDemo />
      </div>
    </MtwProvider>
  )
})
ComponentDemoLayout.displayName = 'ComponentDemoLayout'

interface ComponentDemoProps {
  color: number
  onChange: (newColor: string | IColor) => Promise<void>
  isLoading?: boolean
}

export const ComponentDemo = ({ color, onChange, isLoading }: ComponentDemoProps) => {
  const canEditBrandKit = useOrgPerm(OrgPermission.BrandKitEdit)

  const initialSourceColor = sourceToHex(color)
  const [debSourceColor, _cancel, _setDebounce, sourceColor, setSourceColor] = useDebounceInput(initialSourceColor)

  return (
    <div className='h-full relative flex flex-col gap-6'>
      <section>
        <div className='flex items-center gap-4 mb-6'>
          <div className='flex items-center gap-2'>
            <label htmlFor='colorPicker' className='text-sm'>
              <Trans>Source Color</Trans>
            </label>

            <ColorPickerV2
              color={sourceColor}
              onChange={(color) => setSourceColor(color.hex)}
              onCancel={() => setSourceColor(initialSourceColor)}
              onSave={onChange}
            />
          </div>
        </div>

        <ComponentDemoLayout sourceColor={debSourceColor} />
      </section>
    </div>
  )
}
